import { useState } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import { useAuth } from '../../shared/auth/AuthContext';
import { AuthLayout } from '../components/AuthLayout';
import { endedSession, postLoginPath } from '../../shared/auth/returnUrl';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type FormValues = z.infer<typeof schema>;

export default function LoginPage(): JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const outcome = await login(values.email, values.password);
      const previousSession = endedSession.take();
      if (outcome.status === 'select') {
        navigate('/select-tenant', { replace: true });
        return;
      }
      if (outcome.user.mustCompleteProfile) {
        navigate('/profile', { replace: true });
        return;
      }
      const requested = (location.state as { from?: string } | null)?.from;
      navigate(postLoginPath(requested, outcome.user.id, previousSession), { replace: true });
    } catch {
      setServerError('Email or password is incorrect.');
    }
  });

  return (
    <AuthLayout title="Sign in">
      <Paper sx={{ p: 4, width: '100%', maxWidth: 400 }} elevation={2}>
        <Typography variant="h5" gutterBottom>
          Sign in
        </Typography>
        <Box component="form" onSubmit={onSubmit} noValidate>
          <Stack spacing={2}>
            {serverError && <Alert severity="error">{serverError}</Alert>}
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              autoFocus
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              {...register('email')}
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="current-password"
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
              {...register('password')}
            />
            <Button type="submit" variant="contained" disabled={isSubmitting} size="large">
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Forgot your password?
            </Link>
          </Stack>
        </Box>
      </Paper>
    </AuthLayout>
  );
}
